import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Rocket, Check, ArrowRight, Plug, Boxes, LineChart } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import type { LucideIcon } from 'lucide-react';
import { isBuiltinSource } from '../lib/sources';

// The three steps a new workspace follows: connect a source, review what the
// record now holds, then explore the figures. Each is "done" when the record's
// own state says so - nothing to dismiss - and the widget hides once all three
// are, so an established workspace never sees it.
//
// Unlike the other widgets it is a filled navy/teal card rather than a white
// one, so the first thing a new user sees stands out from the page.
// The candidates Navaris recommends filing; source_id is the one source holding them, if one.
interface Recommended {
  files: number;
  types: number;
  source_id: string | null;
}

interface Step {
  key: string;
  icon: LucideIcon;
  title: string;
  subtitle: string;
  done: boolean;
  to: string;
  action: string;
}

const NONE_RECOMMENDED: Recommended = { files: 0, types: 0, source_id: null };

export default function GettingStarted() {
  const { authFetch } = useAuth();
  const { sources, hasActiveJobs, overviewLoading } = useWorkspace();
  const [counts, setCounts] = useState<{ figures: number; dupes: number; recommended: Recommended } | null>(null);

  useEffect(() => {
    let stale = false;
    Promise.all([
      authFetch('/knowledge/nodes').then(r => (r.ok ? r.json() : [])),
      authFetch('/knowledge/suggestions').then(r => (r.ok ? r.json() : [])),
      authFetch('/sources/candidates/summary').then(r => (r.ok ? r.json() : NONE_RECOMMENDED)),
    ])
      .then(([nodes, dupes, recommended]: [{ label: string; count: number }[], unknown[], Recommended]) => {
        if (stale) return;
        const figures = nodes.find(n => n.label === 'figure')?.count ?? 0;
        setCounts({ figures, dupes: Array.isArray(dupes) ? dupes.length : 0, recommended });
      })
      .catch(() => { if (!stale) setCounts({ figures: 0, dupes: 0, recommended: NONE_RECOMMENDED }); });
    return () => { stale = true; };
  }, [authFetch]);

  // Wait for both the overview and the counts so steps don't flip as data lands.
  if (overviewLoading || counts === null) return null;

  // Navaris is every workspace's own; the first step is connecting one of its systems.
  const connectedSources = sources.filter(s => !isBuiltinSource(s.source_type));
  const connected = connectedSources.length > 0;
  const reviewCandidates = counts.recommended;
  const toReview = reviewCandidates.files + counts.dupes;
  // Right after connecting, the index/sync jobs are still running: that is "working on it".
  const syncing = connected && hasActiveJobs && toReview === 0;

  let reviewSubtitle: string;
  if (reviewCandidates.files > 0) {
    const { files, types } = reviewCandidates;
    reviewSubtitle = `${files} document${files === 1 ? '' : 's'} Navaris recommends filing, under ${types} document type${types === 1 ? '' : 's'}.`;
  } else if (counts.dupes > 0) {
    reviewSubtitle = `${counts.dupes} possible duplicate${counts.dupes === 1 ? '' : 's'} to confirm.`;
  } else if (syncing) {
    reviewSubtitle = 'We are reading your sources now — this can take a few minutes.';
  } else {
    reviewSubtitle = 'Confirm the documents we found and fill any gaps.';
  }

  const steps: Step[] = [
    {
      key: 'connect',
      icon: Plug,
      title: 'Connect your first data source',
      subtitle: connected
        ? `${connectedSources.length} source${connectedSources.length === 1 ? '' : 's'} connected.`
        : 'Link your accounting, banking, CRM and documents so your record can build itself.',
      done: connected,
      to: '/sources/add',
      action: 'Add a source',
    },
    {
      key: 'review',
      icon: Boxes,
      title: 'Review what we found',
      subtitle: reviewSubtitle,
      done: connected && !syncing && toReview === 0,
      to: reviewCandidates.files > 0
        ? reviewCandidates.source_id ? `/sources/${reviewCandidates.source_id}?tab=review` : '/sources'
        : '/knowledge',
      action: syncing ? '' : reviewCandidates.files > 0 ? 'Review candidates' : 'Confirm duplicates',
    },
    {
      key: 'explore',
      icon: LineChart,
      title: 'Explore your metrics',
      subtitle: 'See revenue, burn, runway and more, traced back to the figures behind them.',
      done: counts.figures > 0,
      to: '/model',
      action: 'Open the model',
    },
  ];

  if (steps.every(s => s.done)) return null;
  const current = steps.findIndex(s => !s.done);

  return (
    <section className="rounded-xl p-5 bg-brand-dark text-white shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <Rocket className="w-4 h-4 text-brand shrink-0" />
        <h4 className="text-sm font-semibold">Getting started</h4>
      </div>
      <ol className="space-y-1.5">
        {steps.map((step, i) => {
          const Icon = step.icon;
          const active = i === current;
          return (
            <li
              key={step.key}
              className={`flex items-center gap-3 rounded-lg ${
                active ? 'bg-white shadow-md p-3.5' : 'px-3.5 py-2'
              }`}
            >
              <span
                className={`w-7 h-7 rounded-full shrink-0 flex items-center justify-center ${
                  active ? 'bg-brand text-navy' : step.done ? 'bg-brand/25 text-brand' : 'bg-white/10 text-white/50'
                }`}
              >
                {step.done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              </span>
              <span className="min-w-0 flex-1">
                <span
                  className={`block text-[13px] font-medium ${
                    active ? 'text-graphite' : step.done ? 'text-white/50 line-through' : 'text-white/70'
                  }`}
                >
                  {step.title}
                </span>
                {active && <span className="block text-[12px] text-coolslate mt-0.5">{step.subtitle}</span>}
              </span>
              {step.action && (active ? (
                <Link
                  to={step.to}
                  className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[13px] font-medium bg-brand text-navy hover:bg-brand-dark hover:text-white transition-colors"
                >
                  {step.action}
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              ) : !step.done && (
                <Link to={step.to} className="shrink-0 inline-flex items-center gap-1 text-[12px] text-white/50 hover:text-white">
                  {step.action}
                  <ArrowRight className="w-3 h-3" />
                </Link>
              ))}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
